<template lang="pug">
app-main-content-layout(title='')
  .c-orgs-page
    //- Header Section
    section.c-orgs-page__header
      .c-orgs-page__header-content
        h1.c-orgs-page__heading Organizations
        p.c-orgs-page__subtitle Manage organization accounts and settings

    //- Navigation Tabs Section (Pill Style)
    section.c-orgs-page__nav-section
      .c-orgs-page__nav-wrapper
        .c-orgs-nav
          a.c-orgs-nav-link(
            :class="{ 'c-orgs-nav-link--active': currentView === 'All' }"
            @click.prevent="handleTabChange('All')"
          ) All
          a.c-orgs-nav-link(
            :class="{ 'c-orgs-nav-link--active': currentView === 'Pending' }"
            @click.prevent="handleTabChange('Pending')"
          ) Pending
          a.c-orgs-nav-link(
            :class="{ 'c-orgs-nav-link--active': currentView === 'Approved' }"
            @click.prevent="handleTabChange('Approved')"
          ) Approved
          a.c-orgs-nav-link(
            :class="{ 'c-orgs-nav-link--active': currentView === 'Declined' }"
            @click.prevent="handleTabChange('Declined')"
          ) Declined
          a.c-orgs-nav-link(
            :class="{ 'c-orgs-nav-link--active': currentView === 'Archived' }"
            @click.prevent="handleTabChange('Archived')"
          ) Archived
          a.c-orgs-nav-link(
            :class="{ 'c-orgs-nav-link--active': currentView === 'Invited' }"
            @click.prevent="handleTabChange('Invited')"
          ) Invited

    //- Main Content Card
    section.c-orgs-content-card
      //- Filters
      article.c-orgs-content-card__filters
        OrganizationFilters(
          v-model="textFilter"
          :sort-value="sortBy"
          :sort-options="sortOptions"
          :is-filtered="isFiltered"
          @search="searchFilter"
          @update:sortValue="sortBy = $event"
          @sort="sortTable"
          @clear="clearTextFilter"
        )

    //- Table Section
    section.c-orgs-table-section
      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--name
            .c-skeleton-cell.c-skeleton-cell--email
            .c-skeleton-cell.c-skeleton-cell--onboarded
            .c-skeleton-cell.c-skeleton-cell--active
      
      //- Table
      el-table(
        v-else
        :data="items.items"
        @row-click="routeToOrg($event)"
        style="width: 100%"
        :row-class-name="getRowClassName"
      ).clickable-rows
        el-table-column(width="15")
        el-table-column(label="Name" min-width="250")
          template(#default="scope")
            .lh-sm
              .font-black {{ scope.row.name }}
              .font-light.fs-12(v-if="scope.row.title") {{ scope.row.title }}
        
        el-table-column(label="Email" min-width="200")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.email }}

        el-table-column(label="Onboarded" min-width="100")
          template(#default="scope")
            span.status-badge.status-badge--onboarded(v-if="scope.row.onboarded") Yes
            span.status-badge.status-badge--not-onboarded(v-else) No

        el-table-column(label="Approved" min-width="100" v-if="isPending")
          template(#default="scope")
            span.status-badge.status-badge--approved(v-if="scope.row.approved") Yes
            span.status-badge.status-badge--not-approved(v-else) No

        el-table-column(label="Active" min-width="100")
          template(#default="scope")
            span.status-badge.status-badge--active(v-if="scope.row.active") Yes
            span.status-badge.status-badge--inactive(v-else) No
      
      //- Pagination
      .c-orgs-pagination(v-if="items.items.length > 0")
        .c-orgs-pagination__controls
          el-button.c-orgs-pagination__button(
            @click="handleCurrentChange(currentPage - 1)"
            :disabled="currentPage === 1 || loading"
          ) Prev
          .c-orgs-pagination__info
            span.c-orgs-pagination__page-number Page {{ currentPage }}
            span.c-orgs-pagination__item-count(v-if="items.items.length > 0") Showing {{ startItem }}-{{ endItem }}
          el-button.c-orgs-pagination__button(
            @click="handleCurrentChange(currentPage + 1)"
            :disabled="!hasMorePages || loading"
          ) Next

  dialog-add-user(
    v-if="dialog"
    v-model="dialog"
    :sending-invite="sendingInvite"
    @save-item="organizationInvite"
    :server-error="serverError"
    @close-dialog="dialog=false"
  )
</template>

<script>

import {
  defineComponent, ref,
  onMounted, reactive,
  computed,
} from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import shared from './shared.ts';
import DialogAddUser from './DialogAddUser.vue';
import OrganizationFilters from './OrganizationFilters.vue';

export default defineComponent({
  name: 'ManageContent_Organizations',
  components: { DialogAddUser, OrganizationFilters },
  setup() {
    const {
      router,
      loading,
      getAllOrganizations,
      createOrganizationInvite,
      doGetInvites,
      getOrganizationsV2,
    } = shared();
    const route = useRoute();
    const store = useStore();
    const filter = ref('');
    const items = reactive({ items: [] });
    const invited = reactive({ items: [] });
    const allItems = reactive({ items: [] });
    const textFilter = ref('');
    const isFiltered = ref(false);
    const serverError = ref('');
    const sendingInvite = ref(false);
    const dialog = ref(false);
    const isPending = route.name === 'ManagePendingOrganizations';
    const test = 'pending';
    const sortBy = ref('');
    const sortOptions = [
      { label: 'Name (A-Z)', value: { field: 'Name', asc: true } },
      { label: 'Name (Z-A)', value: { field: 'Name', asc: false } },
      { label: 'Email (A-Z)', value: { field: 'EMail', asc: true } },
      { label: 'Email (Z-A)', value: { field: 'EMail', asc: false } },
    ];
    const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);

    const currentView = ref('All');
    const totalItems = computed(() => {
      // Estimate total items based on current page and hasMorePages
      if (hasMorePages.value) {
        return currentPage.value * pageSize.value + 1;
      }
      return (currentPage.value - 1) * pageSize.value + items.items.length;
    });
    const baseInput = reactive({
      ids: null,
      entity: null,
      entityIds: null,
      isActive: null,
      statuses: null,
      query: null,
      page: 1,
      pageSize: 20,
      excludeUnclaimed: null,
      sortBy: null,
      sortAsc: null,
    });

    const currentPage = ref(1);
    const pageSize = ref(20);
    const hasMorePages = ref(true);

    function getRowClassName(row) {
      if (selectedOrganization.value && row.row.id === selectedOrganization.value.id) {
        return 'selected-row';
      }
      return '';
    }

    function routeToOrg(itm) {
      if (itm.onboarded) {
        localStorage.setItem('selectedOrganization', JSON.stringify(itm));
        store.dispatch('organizations/SET_SELECTED_ORGANIZATION', itm);
        router.push(`/manage/orgs/${itm.id}/edit`);
      } else {
        router.push(`/manage/invites/${itm.id}/info`);
      }
    }

    async function organizationInvite(item) {
      try {
        loading.value = true;
        sendingInvite.value = true;
        await createOrganizationInvite(item);
        dialog.value = false;
        items.items = await getAllOrganizations();
      } catch (err) {
        serverError.value = err.message;
      } finally {
        loading.value = false;
        sendingInvite.value = false;
      }
    }

    function showOnboardedStatus(item) {
      return item.onboarded ? 'Onboarded' : '--';
    }

    function showActiveStatus(item) {
      return item.active ? 'Yes' : 'No';
    }

    function getClass(currentItem) {
      if (currentItem === currentView.value) {
        return 'filter-active';
      }
      return 'filter-inactive';
    }

    async function findInvites() {
      try {
        loading.value = true;
        invited.items = await doGetInvites();
        loading.value = false;
      } catch (err) {
        console.error('Error finding invites:', err);
      }
    }

    async function findOrganizationsV2() {
      try {
        loading.value = true;
        const result = await getOrganizationsV2(baseInput);
        allItems.items = result;
        items.items = result;

        // Check if we have more pages by seeing if we got a full page of results
        hasMorePages.value = result.length === pageSize.value;

        loading.value = false;
      } catch (err) {
        loading.value = false;
      }
    }

    // ________function that filters invites by entity ________
    function filterInvites() {
      items.items = invited.items.filter((item) => item.entity === 'Organization');
    }

    async function sortTable() {
      if (sortBy.value && sortBy.value.value) {
        baseInput.sortBy = sortBy.value.value.field;
        baseInput.sortAsc = sortBy.value.value.asc;
        baseInput.page = 1;
        currentPage.value = 1;
        hasMorePages.value = true;

        if (currentView.value === 'Invited') {
          filterInvites();
        } else {
          await findOrganizationsV2();
        }
      }
    }

    // Computed properties for pagination display
    const startItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + 1;
    });

    const endItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + items.items.length;
    });

    const handleCurrentChange = (page) => {
      if (page < 1 || (page > currentPage.value && !hasMorePages.value)) {
        return;
      }
      currentPage.value = page;
      baseInput.page = page;

      // Preserve current sorting when changing pages
      if (sortBy.value && sortBy.value.value) {
        baseInput.sortBy = sortBy.value.value.field;
        baseInput.sortAsc = sortBy.value.value.asc;
      }

      if (currentView.value === 'Invited') {
        filterInvites();
      } else {
        findOrganizationsV2();
      }
    };

    // ________create function for search filter ________
    async function searchFilter() {
      baseInput.ids = null;
      baseInput.entity = null;
      baseInput.entityIds = null;
      baseInput.isActive = null;
      baseInput.statuses = currentView.value;
      baseInput.query = textFilter.value || null;
      baseInput.page = 1;
      currentPage.value = 1;
      hasMorePages.value = true;
      baseInput.excludeUnclaimed = currentView.value === 'Approved' ? true : null;

      // Preserve current sorting when searching
      if (sortBy.value && sortBy.value.value) {
        baseInput.sortBy = sortBy.value.value.field;
        baseInput.sortAsc = sortBy.value.value.asc;
      }

      if (textFilter.value === '') {
        isFiltered.value = false;
      } else {
        isFiltered.value = true;
      }

      if (currentView.value === 'Invited') {
        filterInvites();
      } else {
        await findOrganizationsV2();
      }
    }

    async function clearTextFilter() {
      textFilter.value = '';
      isFiltered.value = false;
      baseInput.query = null;
      currentPage.value = 1;
      hasMorePages.value = true;
      // Preserve excludeUnclaimed setting for Approved tab
      baseInput.excludeUnclaimed = currentView.value === 'Approved' ? true : null;

      // Preserve current sorting when clearing filter
      if (sortBy.value && sortBy.value.value) {
        baseInput.sortBy = sortBy.value.value.field;
        baseInput.sortAsc = sortBy.value.value.asc;
      }

      if (currentView.value === 'Invited') {
        filterInvites();
      } else {
        await findOrganizationsV2();
      }
    }

    async function headerFilter(filterBy) {
      currentView.value = filterBy;

      baseInput.ids = null;
      baseInput.entity = null;
      baseInput.entityIds = null;
      baseInput.isActive = null;
      baseInput.query = null;
      baseInput.page = 1;
      currentPage.value = 1;
      hasMorePages.value = true;

      // Preserve current sorting when changing filter
      if (sortBy.value && sortBy.value.value) {
        baseInput.sortBy = sortBy.value.value.field;
        baseInput.sortAsc = sortBy.value.value.asc;
      }

      if (filterBy === 'Invited') {
        filterInvites();
      } else {
        baseInput.statuses = filterBy;
        // Set excludeUnclaimed to true only for Approved tab
        baseInput.excludeUnclaimed = filterBy === 'Approved' ? true : null;
        await findOrganizationsV2();
      }
    }

    function handleTabChange(tabName) {
      headerFilter(tabName);
    }

    onMounted(async () => {
      // await findOrganizations(route.name);
      await findInvites();
      if (route.name === 'ManagePendingOrganizations') {
        currentView.value = 'Pending';
        baseInput.statuses = 'Pending';
      }
      await findOrganizationsV2();
    });

    return {
      // data (static)
      loading,
      filter,
      isPending,
      test,
      // data (reactive)
      items,
      serverError,
      dialog,
      sendingInvite,
      selectedOrganization,
      getRowClassName,
      getClass,
      headerFilter,
      currentView,
      invited,
      allItems,
      textFilter,
      isFiltered,
      currentPage,
      pageSize,
      hasMorePages,
      startItem,
      endItem,
      totalItems,
      // methods
      handleCurrentChange,
      showOnboardedStatus,
      showActiveStatus,
      organizationInvite,
      routeToOrg,
      findInvites,
      searchFilter,
      clearTextFilter,
      sortBy,
      sortTable,
      sortOptions,
      baseInput,
      filterInvites,
      handleTabChange,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables - Match Dashboard.vue and Activity page
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-orgs-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__nav-section {
    margin-top: 0;
    margin-bottom: 0;
  }

  &__nav-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }
}

// Navigation Tabs (Pill Style) - Match Activity page
.c-orgs-nav {
  display: inline-flex;
  height: 44px;
  align-items: center;
  justify-content: center;
  background-color: $gray-100;
  border-radius: 12px;
  padding: 4px;
  gap: 0;
  flex-wrap: nowrap;
}

.c-orgs-nav-link {
  display: inline-flex;
  height: calc(100% - 2px);
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 10px;
  border: 1px solid transparent;
  padding: 4px 24px;
  font-size: 0.9375rem;
  font-weight: 500;
  color: $gray-700;
  white-space: nowrap;
  transition: color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: $gray-900;
  }

  &--active {
    background-color: white;
    color: $gray-900;
    font-weight: 600;
    border-color: transparent;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  }
}

// Main Content Card
.c-orgs-content-card {
  background: transparent;
  border-radius: 0;
  border: none;
  box-shadow: none;
  overflow: visible;

  &__filters {
    padding: 0;
    border-bottom: none;
  }
}

// Table Section - Match Activity page exactly
.c-orgs-table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  overflow: hidden;

  // Enable horizontal scrolling
  :deep(.el-table) {
    min-width: 100%;
    border-radius: 0;
  }

  :deep(.el-table__body-wrapper) {
    overflow-x: auto;
  }

  // Table styling - Match Activity page
  :deep(.el-table) {
    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      white-space: nowrap;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0.625rem 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

// Selected row highlight
.selected-row {
  :deep(td) {
    background-color: rgba(67, 73, 221, 0.08) !important;
  }
}

// Pagination - Simple prev/next style
.c-orgs-pagination {
  display: flex;
  justify-content: center;
  padding: 1.5rem;

  &__controls {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }

  &__button {
    border-radius: 0.375rem;
    font-weight: 500;
    transition: all 0.15s ease;
    background-color: transparent;
    border: 1px solid $gray-300;
    color: $gray-700;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;

    &:hover:not(:disabled) {
      background-color: transparent;
      border-color: $gray-500;
      color: $gray-900;
    }

    &:disabled {
      border-color: $gray-300;
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    min-width: 140px;
  }

  &__page-number {
    font-size: 0.875rem;
    font-weight: 600;
    color: $gray-900;
  }

  &__item-count {
    font-size: 0.75rem;
    color: $gray-600;
  }

  @media (max-width: 640px) {
    &__controls {
      flex-direction: column;
      gap: 1rem;
      width: 100%;
    }

    &__button {
      width: 100%;
    }

    &__info {
      order: -1;
    }
  }
}

// Skeleton Loader - Match Activity page
.c-skeleton-loader {
  width: 100%;
  padding: 1rem 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  &--name {
    width: 200px;
    flex-shrink: 0;
  }

  &--email {
    width: 180px;
    flex-shrink: 0;
  }

  &--onboarded {
    width: 80px;
    flex-shrink: 0;
  }

  &--active {
    width: 80px;
    flex-shrink: 0;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Status badges - Match Activity page badge style exactly
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  border: 1px solid;

  &--active {
    background-color: #D1F4F0;
    color: #0D9488;
    border-color: #14B8A6;
  }

  &--inactive {
    background-color: $gray-100;
    color: $gray-600;
    border-color: $gray-300;
  }

  &--onboarded {
    background-color: #D1F4F0;
    color: #0D9488;
    border-color: #14B8A6;
  }

  &--not-onboarded {
    background-color: $gray-100;
    color: $gray-600;
    border-color: $gray-300;
  }

  &--approved {
    background-color: #D1F4F0;
    color: #0D9488;
    border-color: #14B8A6;
  }

  &--not-approved {
    background-color: #FFE8D6;
    color: #D97706;
    border-color: #FB923C;
  }
}

// Font utilities
.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.font-light {
  font-weight: 400;
  color: $gray-600;
}

.lh-sm {
  line-height: 1.25;
}

.fs-12 {
  font-size: 0.75rem;
}

// Responsive Design
@media (max-width: 768px) {
  .c-orgs-page {
    padding: 1.5rem;

    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    &__heading {
      font-size: 1.5rem;
    }

    &__subtitle {
      font-size: 0.8125rem;
    }
  }

  .c-orgs-nav {
    width: 100%;
    overflow-x: auto;
  }

  .c-orgs-nav-link {
    font-size: 0.875rem;
    padding: 4px 16px;
  }
}

@media (max-width: 480px) {
  .c-orgs-page {
    padding: 1rem;

    &__heading {
      font-size: 1.25rem;
    }

    &__subtitle {
      font-size: 0.75rem;
    }
  }
}
</style>
